// Global "which household card is open" state, so any place in the app
// (Insurance, Dashboard, future client portal page, etc) can open the
// household detail modal by passing a household_id.
//
// The modal itself is mounted ONCE at the app root via HouseholdDetailHost,
// which derives the full household object from the clients context.

import { createContext, useContext, useCallback, useState } from 'react';
import { useClients } from './ClientsContext';
import HouseholdDetail from '../components/BWMClients/HouseholdDetail';

const HouseholdDetailContext = createContext(null);

export function HouseholdDetailProvider({ children }) {
  const [openHouseholdId, setOpenHouseholdId] = useState(null);
  // Optional action handlers passed in by the opener. Insurance / Dashboard
  // entry points open with no handlers (read-only); the Households tab
  // passes its full edit/split/rename/merge handlers so the modal stays
  // fully functional when opened from there.
  const [handlers, setHandlers] = useState({});

  const openHousehold = useCallback((householdId, handlersArg = {}) => {
    if (!householdId) return;
    setOpenHouseholdId(householdId);
    setHandlers(handlersArg || {});
  }, []);

  const closeHousehold = useCallback(() => {
    setOpenHouseholdId(null);
    setHandlers({});
  }, []);

  return (
    <HouseholdDetailContext.Provider value={{ openHouseholdId, openHousehold, closeHousehold }}>
      {children}
      <HouseholdDetailHost handlers={handlers} />
    </HouseholdDetailContext.Provider>
  );
}

export function useHouseholdDetail() {
  const ctx = useContext(HouseholdDetailContext);
  if (!ctx) throw new Error('useHouseholdDetail must be used within HouseholdDetailProvider');
  return ctx;
}

// Build the household object that HouseholdDetail expects from a flat
// clients array. Mirrors the grouping logic in BWMClients.
function buildHouseholdFromClients(householdId, allClients) {
  const entries = (allClients || []).filter(c => c.householdId === householdId);
  if (entries.length === 0) {
    // Fallback: a stub household so the modal doesn't crash when an Insurance
    // policy points at a household_id with no client records.
    return {
      id: householdId,
      name: householdId,
      entries: [],
      divisions: [],
      isActive: false,
    };
  }
  const name = entries[0]?.householdName || entries[0]?.name || householdId;
  const divisions = Array.from(new Set(entries.map(e => e.division).filter(Boolean)));
  const isActive = entries.some(e => e.status === 'Active');
  return { id: householdId, name, entries, divisions, isActive };
}

function HouseholdDetailHost({ handlers }) {
  const { openHouseholdId, closeHousehold } = useHouseholdDetail();
  const { clients } = useClients();
  if (!openHouseholdId) return null;
  const household = buildHouseholdFromClients(openHouseholdId, clients);
  if (!household) return null;
  return (
    <HouseholdDetail
      household={household}
      onClose={closeHousehold}
      openEditForm={handlers.openEditForm}
      splitFromHousehold={handlers.splitFromHousehold}
      renameHousehold={handlers.renameHousehold}
      onMergeStart={handlers.onMergeStart}
      openAddService={handlers.openAddService}
    />
  );
}
